import { SelectOption } from '../../../ui/gzSelect/types';
import { ViewConfiguration, ViewType } from '../../gridView/types';
import { FAVORITE_ICON_STATE } from '../../utils/constants';
import { GridColumnConfiguration } from '@nirvana/shared/dtos/types/shared/grid';
import { GridViewWebComponent } from '../../gridView';
import { GenericGridSmartViewsWebComponent } from '../component/GenericGridSmartViewsWebComponent';

export interface GenericGridSmartViewsWebComponentSchema {
  categoriesConfig: CategoriesConfig;
  data: Array<SmartViewItemConfiguration>;
  settings: SmartViewState;
  hasSearchInput: boolean;
  emptySavedViewsText?: string;
  searchPlaceholderString?: string;
  initialView?: InitialView;
  customErrorMappingFn: (errorCode: number) => string | null;
  searchFn: ((string, SmartViewItemConfiguration) => boolean) | null;
  decoratedTextOnSearchFn: ((string, SmartViewItemConfiguration) => string) | null;
}

export interface InitialView {
  isActiveViewOnLoad?: true;
  view?: InitialSmartViewItemConfiguration;
  category?: string;
}

export interface InitialSmartViewItemConfiguration {
  label: string;
  specifics: any;
}

export interface GenericSmartViewsData {
  _id: string;
  userId: string;
  companyId: string;
  componentName: string;
  label: string;
  isReadOnly: boolean;
  isFavorite: boolean;
  isRecent: boolean;
  category: string;
  lastUpdated: string;
  lastUsed: string;
  specifics: object;
}
export enum Categories {
  FAVORITES = 'favorites',
  RECENT = 'recent',
  SAVED = 'saved',
  DEFAULT = 'default',
}

export enum SmartViewType {
  GRID_VIEW = 1,
  LIVE_SEARCH = 2,
  NETWORK_V2 = 3,
}

export interface SmartViewItemConfiguration {
  _id: string;
  componentName: string;
  label: string;
  originalLabel?: string;
  userId: string;
  companyId?: string;
  type: SmartViewType;
  isReadOnly: boolean;
  isFavorite?: boolean;
  lastUpdated?: string;
  lastUsed?: string;
  category?: string;
  isRecent?: boolean;
  specifics: any;
}

export interface SmartViewGridConfig {
  id: string;
  type: ViewType;
  label: string;
  isReadOnly: boolean;
  isFavorite?: boolean;
  isRecent?: boolean;
  filters: Array<any>;
  displayedColumns: Array<number | string>;
  compactView: boolean;
  columnConfigs: Array<GridColumnConfiguration>;
  pageSize: number;
  isDefault: boolean;
}

export enum SmartViewInlineActions {
  RENAME = 'rename',
  DELETE = 'delete',
  SAVE_AS = 'saveAs',
}

export enum SmartViewEvents {
  SMART_VIEW_SELECTED = 'smartViewSelected',
  SMART_VIEW_NAME_UPDATED = 'smartViewNameUpdated',
  SMART_VIEW_DELETED = 'smartViewDeleted',
  SMART_VIEW_CREATED = 'smartViewCreated',
  SMART_VIEW_SAVED = 'smartViewSaved',
  SHOW_SAVE_VIEW_AS_DIALOG = 'showSaveViewAsDialog',
  SMART_VIEW_FAVORITE_CHANGED = 'smartViewFavoriteChanged',
  SMART_VIEW_AFTER_CREATE = 'smartViewAfterCreate',
  SMART_VIEW_AFTER_RECENT_CREATE = 'smartViewAfterRecentCreate',
  SMART_VIEW_CATEGORY_OPEN = 'smartViewCategoryOpen',
  SMART_VIEW_AFTER_DELETE = 'smartViewAfterDelete',
  SMART_VIEWS_RENDERED = 'smartViewsRendered',
}

export interface SmartViewCategory {
  type: string;
  sectionTitle: string;
  isOpen?: boolean;
  inlineActions: Array<SelectOption>;
  items: Array<SmartViewItemConfiguration>;
  smartViewValidators?: Array<any>;
  smartViewMaxInputLength?: number;
  noSpaceEscape?: boolean;
  emptyStateItemText?: string;
}
export interface SmartViewsUpdateRequest {
  id: string;
  details: {
    label?: string;
    specifics?: object;
  };
}

export interface SmartViewStateGetResponse {
  _id: string;
  name: string;
  userId: string;
  value: string;
}
export interface SmartViewsCreateRequest {
  label?: string;
  category: string;
  type: number;
  specifics: object;
}

export interface SmartViewsRecentCreateRequest {
  label?: string;
  category: string;
  type: number;
  specifics: any;
}

export interface State {
  selectedViewId: string;
}
export interface StateGetResponse {
  _id: string;
  name: string;
  userId: string;
  value: string;
}

export interface SmartViewState {
  selectedViewId: string | null;
  panelSize?: number;
  isCollapsed?: boolean;
  // be aware this field with its content is stored in 'states' collection. Renaming it without further adjustments will
  // cause bugs. See here: DataProvider/DAL/Models/State/State.php -'removeUnsavedChangesFromNetworkIfNecessary()'
  savedViewConfiguration?: Object;
}

export interface CategoriesConfig {
  customCategories: Array<SmartViewCategory>;
  internalCategories: InternalCategories;
}

export interface InternalCategories {
  recent: InternalCategory;
  default: InternalCategory;
  favorites: InternalCategory;
}

export interface InternalCategory {
  isAvailable: boolean;
  sectionTitle?: string;
  emptyStateItemText?: string;
  isOpen?: boolean;
  translations?: Object;
}
export interface UpdateSmartViewSettingsRequest {
  selectedViewId: string | null;
  panelSize?: number;
  isCollapsed?: boolean;
}
export interface ValidateViewNameResponse {
  valid: boolean;
  errorCode: number;
}

export enum ValidateViewNameErrorCodeResponse {
  INVALID_VIEWNAME_LENGTH_CODE = 1,
  INVALID_VIEWNAME_DUPLICATE_NAME_CODE = 2,
  INVALID_VIEWNAME_INVALID_CHARACTERS = 3,
}

export enum SmartViewType {
  GRID = 1,
  CUSTOM = 2,
}

export function convertSmartViewToGridView<Column extends number, ColumnKeys extends string>(
  smartView: SmartViewItemConfiguration
): ViewConfiguration<Column, ColumnKeys> {
  return {
    id: smartView._id,
    name: smartView.label,
    type: smartView.isReadOnly ? ViewType.DEFAULT : ViewType.CUSTOM,
    filters: JSON.parse(smartView.specifics.filters),
    displayedColumns: smartView.specifics.displayedColumns,
    compactView: smartView.specifics.compactView,
    pageSize: smartView.specifics.pageSize,
    isFavorite: smartView.isFavorite,
    ...(smartView.specifics.columnConfigs ? { columnConfigs: smartView.specifics.columnConfigs } : {}),
  };
}

export function convertGenericViewConfigToGridViewConfig(viewConfig) {
  // the grid only understands 2 view types , default and custom. These are used to enable/disabled
  // action buttons (Save As, Save, Discard Changes)
  // so we need to convert our current view types to the types that the grid can understand
  let type = ViewType.CUSTOM;
  if (viewConfig.isReadOnly) {
    type = ViewType.DEFAULT;
  }
  return {
    id: viewConfig._id,
    type: type,
    label: viewConfig.label,
    isFavorite: viewConfig.isFavorite,
    isRecent: viewConfig.isRecent,
    isReadOnly: viewConfig.isReadOnly,
    filters: JSON.parse(viewConfig.specifics.filters),
    displayedColumns: viewConfig.specifics.displayedColumns,
    compactView: viewConfig.specifics.compactView,
    columnConfigs: viewConfig.specifics.columnConfigs,
    pageSize: viewConfig.specifics.pageSize,
  };
}

export function configureFavoriteIcon(viewConfiguration) {
  const favoriteIconConfig = {
    icon: FAVORITE_ICON_STATE.OUTLINE,
    disabled: true,
    active: false,
  };
  if (viewConfiguration.isFavorite) {
    favoriteIconConfig.icon = FAVORITE_ICON_STATE.FILLED;
    favoriteIconConfig.disabled = false;
    favoriteIconConfig.active = true;
  }
  if (viewConfiguration.isReadOnly) {
    favoriteIconConfig.icon = FAVORITE_ICON_STATE.OUTLINE;
    favoriteIconConfig.disabled = true;
    favoriteIconConfig.active = false;
  }
  if (!viewConfiguration.isFavorite && !viewConfiguration.isRecent && !viewConfiguration.isReadOnly) {
    favoriteIconConfig.icon = FAVORITE_ICON_STATE.OUTLINE;
    favoriteIconConfig.disabled = false;
    favoriteIconConfig.active = false;
  }
  return favoriteIconConfig;
}

export interface SmartViewElementsConfig {
  gridElement: GridViewWebComponent<number, string>;
  smartViewsElement: GenericGridSmartViewsWebComponent;
}
